'use client';

import { useLocale } from 'next-intl';
import { useEffect } from 'react';
import {
  beaconEventView,
  beaconNewsView,
  isAnalyticsEnabled,
  resolveViewerCountry,
  trackEventView,
  trackNewsView,
} from '@/lib/api/analytics/client';
import { consumeNavSource } from '@/lib/api/analytics/navSource';
import type { AnalyticsContentType } from '@/lib/api/analytics/types';
import { ensureVisitorId } from '@/lib/api/analytics/visitor';

const HEARTBEAT_INTERVAL_MS = 10000;
const MAX_TRACKED_SEC = 180;

/**
 * Optional enrichment sent alongside a view. `title`/`country` apply to both types;
 * `locale`/`documentId` are news-only. Omit `country` to have it resolved client-side.
 */
export interface AnalyticsEnrich {
  title?: string;
  country?: string;
  /** News only — Strapi locale (`lc`) of the viewed entry. */
  locale?: string;
  /** News only — Strapi documentId (`di`), shared across languages. */
  documentId?: string;
}

function computeScrollPct(): number {
  const scrolled = window.scrollY + window.innerHeight;
  const total = document.documentElement.scrollHeight;
  if (total <= 0) return 0;
  const pct = Math.round((scrolled / total) * 100);
  return Math.max(0, Math.min(100, pct));
}

function useAnalyticsSession(
  type: AnalyticsContentType,
  contentId: string | undefined,
  enrich: AnalyticsEnrich = {},
): void {
  const { title, country, locale, documentId } = enrich;
  // Events aren't translated, so their `locale` is the viewer's UI language
  // (news sends its Strapi content locale via `enrich.locale` instead).
  const uiLocale = useLocale();
  useEffect(() => {
    if (!contentId) return;
    if (!isAnalyticsEnabled()) return;

    if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') return;
    const sessionId = crypto.randomUUID();
    // Anonymous visitor id — attached to every call so beacons (which cannot carry
    // the JWT header) still attribute to the right visitor/account.
    const visitorId = ensureVisitorId();

    // Navigation origin (where the user came from) — resolved once per session mount.
    const nav = consumeNavSource();

    // Prefer a server-computed country; otherwise resolve it client-side. The first send
    // is held until this resolves (see startSession) so no hit goes out without a country.
    let resolvedCountry = country;

    let accumulatedActiveMs = 0;
    let lastVisibleStart: number | null =
      document.visibilityState === 'visible' ? Date.now() : null;
    let maxScrollPct = computeScrollPct();
    let stopped = false;
    // Guards the single terminal `finished: true` beacon so the overlapping
    // pagehide / beforeunload / unmount handlers don't each fire one.
    let finalSent = false;
    // Flips once the initial send fires; gates heartbeats/flushes so nothing leaves
    // before the country is known.
    let started = false;
    let interval: number | undefined;

    const getTimeSpentSec = (): number => {
      const ongoing = lastVisibleStart != null ? Date.now() - lastVisibleStart : 0;
      return Math.floor((accumulatedActiveMs + ongoing) / 1000);
    };

    const buildPayload = (finished: boolean) => {
      const timeSpentSec = Math.min(getTimeSpentSec(), MAX_TRACKED_SEC);
      const shared = {
        timeSpentSec,
        maxScrollPct,
        finished,
        ...(visitorId ? { visitorId } : {}),
        ...(title ? { title } : {}),
        ...(resolvedCountry ? { country: resolvedCountry } : {}),
        ...(nav.source ? { source: nav.source } : {}),
        ...(nav.sourceId ? { sourceId: nav.sourceId } : {}),
      };
      if (type === 'event') {
        return {
          sessionId,
          eventId: contentId,
          ...shared,
          ...(uiLocale ? { locale: uiLocale } : {}),
        };
      }
      return {
        sessionId,
        newsId: Number(contentId),
        ...shared,
        ...(locale ? { locale } : {}),
        ...(documentId ? { newsDocumentId: documentId } : {}),
      };
    };

    // Once we've reported the 3-minute cap, the user clearly likes the content —
    // further heartbeats add no signal, so stop the loop.
    const markStoppedIfCapReached = (timeSpentSec: number) => {
      if (timeSpentSec < MAX_TRACKED_SEC) return;
      stopped = true;
      if (interval !== undefined) {
        window.clearInterval(interval);
        interval = undefined;
      }
    };

    const sendFetch = (finished = false) => {
      if (stopped) return;
      const payload = buildPayload(finished);
      if (type === 'event') {
        void trackEventView(payload as Parameters<typeof trackEventView>[0]);
      } else {
        void trackNewsView(payload as Parameters<typeof trackNewsView>[0]);
      }
      markStoppedIfCapReached(payload.timeSpentSec);
    };

    // Terminal send: fires exactly once with finished=true (so the backend marks
    // the session complete), even after the cap has stopped the heartbeat loop.
    const sendFinalBeacon = () => {
      if (finalSent) return;
      finalSent = true;
      const payload = buildPayload(true);
      if (type === 'event') {
        beaconEventView(payload as Parameters<typeof beaconEventView>[0]);
      } else {
        beaconNewsView(payload as Parameters<typeof beaconNewsView>[0]);
      }
    };

    const onScroll = () => {
      const next = computeScrollPct();
      if (next > maxScrollPct) maxScrollPct = next;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        lastVisibleStart = Date.now();
      } else {
        if (lastVisibleStart != null) {
          accumulatedActiveMs += Date.now() - lastVisibleStart;
          lastVisibleStart = null;
        }
        // Flush current state to the server before the tab fully backgrounds.
        if (started) sendFetch();
      }
    };

    const onPageLeave = () => {
      // Final state via beacon — survives unload, fetch wouldn't.
      sendFinalBeacon();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', onPageLeave);
    window.addEventListener('pagehide', onPageLeave);

    const startSession = () => {
      // A resolve landing after page-leave/unmount must not start a stray session.
      if (started || finalSent) return;
      started = true;
      sendFetch(); // initial send — backend now sees the session start with its country
      interval = window.setInterval(() => {
        if (document.visibilityState === 'visible') sendFetch();
      }, HEARTBEAT_INTERVAL_MS);
    };

    // Country already server-side → start now (no delay). Otherwise wait one geolocation
    // round-trip (memoized, so only the first tracked page pays it), starting even on
    // failure so tracking is never blocked.
    if (resolvedCountry) {
      startSession();
    } else {
      void resolveViewerCountry().then((c) => {
        if (c) resolvedCountry = c;
        startSession();
      });
    }

    return () => {
      if (interval !== undefined) window.clearInterval(interval);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('beforeunload', onPageLeave);
      window.removeEventListener('pagehide', onPageLeave);
      // Final beacon on SPA route change / unmount.
      sendFinalBeacon();
    };
  }, [type, contentId, title, country, locale, documentId, uiLocale]);
}

export function useTrackEventView(eventId: string | undefined, enrich?: AnalyticsEnrich): void {
  useAnalyticsSession('event', eventId, enrich);
}

export function useTrackNewsView(newsId: string | undefined, enrich?: AnalyticsEnrich): void {
  useAnalyticsSession('news', newsId, enrich);
}
